/**
 * Listado de clientes.
 *
 * La primera pantalla del ERP con datos de verdad. Sirve de patrón para el
 * resto: el estado de la tabla vive en la URL, la búsqueda la resuelve el
 * servidor y la paginación es por cursor, como la API.
 */

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { useSession } from '../session/session-context.js';
import { ApiError } from '../session/api-error.js';
import { DataTable, type Column } from '../table/data-table.js';
import { decodeTableState, encodeTableState, type TableState } from '../table/table-state.js';

interface CustomerRow {
  id: string;
  code: string | null;
  code_provisional: boolean;
  tax_id: string;
  legal_name: string;
  trade_name: string | null;
  status: string;
  email: string | null;
  phone: string | null;
  sales_rep_user_id: string | null;
  sales_rep_name: string | null;
  site_count: string;
}

interface CustomerPage {
  items: CustomerRow[];
  nextCursor: string | null;
}

/**
 * Los cuatro estados de un cliente (migración 0006).
 *
 * `BLOCKED` **no** es «inactivo»: es «no le sirvas», normalmente por
 * impago. Meter los dos en el mismo saco haría que un administrativo
 * planificara trabajo para un cliente bloqueado.
 */
const STATUS: Record<string, { label: string; icon: string; tone: string }> = {
  ACTIVE: { label: 'Activo', icon: '✓', tone: 'ok' },
  PROSPECT: { label: 'Potencial', icon: '◦', tone: 'info' },
  INACTIVE: { label: 'Inactivo', icon: '•', tone: 'off' },
  BLOCKED: { label: 'Bloqueado', icon: '⚠', tone: 'bad' },
  // Un estado que el servidor añada y el ERP no conozca se enseña tal cual
  // en lugar de disfrazarse del más parecido.
  UNKNOWN: { label: 'Sin clasificar', icon: '?', tone: 'off' },
};

const COLUMNS: Column<CustomerRow>[] = [
  {
    id: 'code',
    label: 'Código',
    // Los que vienen de un contrato firmado en campo llevan un código de andar
    // por casa; se marcan aquí para que la oficina no los pase por alto.
    render: (r) =>
      r.code_provisional ? (
        <span title="Código provisional del terminal: falta asignarle el definitivo">
          {r.code ?? '—'} <span className="pill pill--warn">Prov.</span>
        </span>
      ) : (
        (r.code ?? '—')
      ),
    width: '10rem',
  },
  { id: 'legalName', label: 'Razón social', render: (r) => r.legal_name },
  { id: 'tradeName', label: 'Nombre comercial', render: (r) => r.trade_name ?? '—' },
  { id: 'taxId', label: 'NIF', render: (r) => r.tax_id, width: '9rem' },
  {
    id: 'status',
    label: 'Estado',
    width: '8rem',
    render: (r) => {
      const state = STATUS[r.status] ?? STATUS['UNKNOWN']!;
      // Nunca sólo color (ERP-UX.md §9): color + icono + texto.
      return <span className={`pill pill--${state.tone}`}>{`${state.icon} ${state.label}`}</span>;
    },
    exportValue: (r) => (STATUS[r.status] ?? STATUS['UNKNOWN']!).label,
  },
  {
    id: 'salesRep',
    label: 'Comercial',
    width: '11rem',
    /**
     * «Sin asignar» se marca, no se deja en blanco: un cliente sin comercial
     * es una comisión que no se paga a nadie y una cartera sin repartir, y
     * los huecos silenciosos no se reparten nunca.
     */
    render: (r) =>
      r.sales_rep_name ?? <span className="due--none">Sin asignar</span>,
    exportValue: (r) => r.sales_rep_name ?? 'Sin asignar',
  },
  { id: 'siteCount', label: 'Centros', numeric: true, width: '6rem', render: (r) => r.site_count },
  { id: 'email', label: 'Correo', render: (r) => r.email ?? '—' },
  { id: 'phone', label: 'Teléfono', render: (r) => r.phone ?? '—' },
];

const COLUMN_IDS = COLUMNS.map((c) => c.id);

export function CustomersScreen(): React.ReactNode {
  const { api } = useSession();
  const navigate = useNavigate();
  const [params, setParams] = useSearchParams();

  const state = useMemo(() => decodeTableState(params, COLUMN_IDS), [params]);

  const [reps, setReps] = useState<{ id: string; full_name: string; customer_count: string }[]>([]);
  const [rows, setRows] = useState<CustomerRow[]>([]);
  const [cursor, setCursor] = useState<string | null>(null);
  const [hasMore, setHasMore] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  /**
   * Descarta las respuestas de una búsqueda ya superada.
   *
   * Al teclear «hotel» salen cinco peticiones y no vuelven en orden: sin
   * este contador, la respuesta de «hote» podría llegar después de la de
   * «hotel» y pintar un resultado que no corresponde a lo que hay escrito.
   */
  const request = useRef(0);

  useEffect(() => {
    let cancelled = false;
    void api
      .get<{ items: { id: string; full_name: string; customer_count: string }[] }>(
        '/customers/sales-reps',
      )
      .then((data) => {
        if (!cancelled) setReps(data.items);
      })
      .catch(() => undefined);
    return () => {
      cancelled = true;
    };
  }, [api]);

  const load = useCallback(
    async (from: string | null, append: boolean) => {
      const ticket = ++request.current;
      setLoading(true);

      const query = new URLSearchParams();
      if (state.search.trim()) query.set('search', state.search.trim());
      const status = state.filters['status'];
      if (status?.operator === 'eq' && status.values[0]) query.set('status', status.values[0]);
      const rep = state.filters['salesRep'];
      if (rep?.operator === 'eq' && rep.values[0]) query.set('salesRep', rep.values[0]);
      if (from) query.set('cursor', from);
      query.set('limit', '100');

      try {
        const page = await api.get<CustomerPage>(`/customers?${query.toString()}`);
        if (ticket !== request.current) return;

        setRows((previous) => (append ? [...previous, ...page.items] : page.items));
        setCursor(page.nextCursor);
        setHasMore(page.nextCursor !== null);
        setError(null);
      } catch (caught) {
        if (ticket !== request.current) return;
        setError(caught instanceof ApiError ? caught.message : 'No se han podido cargar los clientes.');
      } finally {
        if (ticket === request.current) setLoading(false);
      }
    },
    [api, state.search, state.filters],
  );

  // La búsqueda espera a que la persona deje de teclear: una petición por
  // pulsación satura la API y, encima, parpadea.
  useEffect(() => {
    const timer = setTimeout(() => void load(null, false), 250);
    return () => clearTimeout(timer);
  }, [load]);

  const onStateChange = useCallback(
    (next: TableState) => {
      // `replace` y no `push`: cada letra tecleada no puede ser un paso
      // atrás del navegador.
      setParams(encodeTableState(next), { replace: true });
    },
    [setParams],
  );

  return (
    <section className="screen">
      <header className="screen__head">
        <h1 className="screen__title">Clientes</h1>
        <Link className="button button--primary button--small" to="/clientes/nuevo">
          Nuevo cliente
        </Link>
      </header>

      {error !== null && (
        <p className="screen__error" role="alert">
          {error}
        </p>
      )}

      {reps.length > 0 && (
        <label className="screen__filter">
          Cartera
          <select
            value={state.filters['salesRep']?.values[0] ?? ''}
            onChange={(event) => {
              const value = event.target.value;
              const filters = { ...state.filters };
              if (value === '') delete filters['salesRep'];
              else filters['salesRep'] = { operator: 'eq', values: [value] };
              onStateChange({ ...state, filters });
            }}
          >
            <option value="">Todas</option>
            {/* Los que no comisiona nadie: la lista con la que se reparte. */}
            <option value="SIN_ASIGNAR">Sin asignar</option>
            {reps.map((r) => (
              <option key={r.id} value={r.id}>
                {r.full_name} ({r.customer_count})
              </option>
            ))}
          </select>
        </label>
      )}

      <DataTable
        columns={COLUMNS}
        rows={rows}
        idOf={(r) => r.id}
        state={state}
        onStateChange={onStateChange}
        exportName="clientes"
        loading={loading}
        hasMore={hasMore}
        onLoadMore={() => void load(cursor, true)}
        onOpenRow={(row) => void navigate(`/clientes/${row.id}`)}
        emptyMessage={
          state.search
            ? 'Ningún cliente coincide con la búsqueda.'
            : 'Todavía no hay clientes dados de alta.'
        }
      />
    </section>
  );
}
